import { useEffect, useRef, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";
import { useConversation } from "@/hooks/useApiData";
import { guestApi } from "@/lib/api";

const MAX = 1000;

export const Conversation = () => {
  const { bookingId } = useParams();
  const { thread: initial, loading } = useConversation(bookingId);

  const [messages, setMessages] = useState(initial?.messages || []);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (initial?.messages) setMessages(initial.messages);
  }, [initial]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages]);

  if (loading) {
    return (
      <div className="flex h-[calc(100svh-180px)] animate-pulse flex-col">
        <div className="flex items-center gap-4 border-b border-foreground/10 pb-5">
          <div className="h-9 w-9 rounded-full bg-muted" />
          <div className="h-12 w-14 bg-muted" />
          <div className="flex-1"><div className="h-5 w-48 bg-muted" /><div className="mt-2 h-3 w-72 bg-muted" /></div>
        </div>
        <div className="flex-1 space-y-6 py-8">
          <div className="h-20 w-2/3 rounded-2xl bg-muted" />
          <div className="ml-auto h-24 w-1/2 rounded-2xl bg-muted" />
          <div className="h-16 w-3/5 rounded-2xl bg-muted" />
        </div>
        <div className="border-t border-foreground/10 pt-4"><div className="h-16 bg-muted" /></div>
      </div>
    );
  }
  if (!initial?.booking) return <Navigate to="/account/messages" replace />;

  const booking = initial.booking;

  const send = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    if (!initial.open) {
      toast.error("Messaging is closed 24 hours after check-out.");
      return;
    }
    setSending(true);
    const optimistic = { id: `local-${Date.now()}`, from: "guest", body, at: "Just now" };
    setMessages((m) => [...m, optimistic]);
    setDraft("");
    try {
      const res = await guestApi.sendBookingMessage(bookingId, { message: body });
      const saved = res?.unwrapped;
      if (saved?.id) {
        setMessages((items) => items.map((item) => item.id === optimistic.id ? {
          ...saved,
          from: "guest",
          body: saved.message,
          at: saved.created_at ? new Date(saved.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "Just now",
        } : item));
      }
    } catch (err) {
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      setDraft(body);
      toast.error(err?.response?.data?.message || err?.apiMessage || "Could not send message.");
    }
    setSending(false);
  };

  return (
    <div data-testid="conversation-root" className="flex h-[calc(100svh-180px)] flex-col">
      <header className="flex items-center gap-4 border-b border-foreground/10 pb-5">
        <Link to="/account/messages" className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-foreground/15 text-foreground hover:bg-foreground/5"><ArrowLeft size={14} /></Link>
        <img src={initial.property.images[0]} alt="" className="h-12 w-14 object-cover" />
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-lg font-medium tracking-tight md:text-xl">{initial.property.name}</p>
          <p className="mt-0.5 truncate text-[11px] uppercase tracking-[0.15em] text-muted-foreground">{initial.host.name} · Ref {booking.reference || booking.id} · {initial.stayDates}</p>
        </div>
        <Link to={`/properties/${initial.property.id}`} className="hidden rounded-full border border-foreground/15 px-4 py-2 text-xs font-medium hover:border-foreground md:inline-flex">Property</Link>
      </header>

      <div ref={listRef} data-testid="conversation-messages" className="flex-1 space-y-6 overflow-y-auto py-8">
        {messages.length === 0 && <div className="py-16 text-center text-sm text-muted-foreground">No messages yet. Start the conversation below.</div>}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.from === "guest" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] px-5 py-3 md:max-w-[70%] ${m.from === "guest" ? "rounded-2xl rounded-br-sm bg-primary text-primary-foreground" : "rounded-2xl rounded-bl-sm bg-muted text-foreground"}`}>
              <p className="text-sm leading-relaxed">{m.body}</p>
              <p className={`mt-2 text-[10px] uppercase tracking-[0.15em] ${m.from === "guest" ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{m.from === "guest" ? "You" : initial.host.name} · {m.at}</p>
            </div>
          </div>
        ))}
      </div>

      {initial.open ? (
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-foreground/10 pt-4">
          <div className="flex items-end gap-3">
            <textarea data-testid="conversation-composer" value={draft} onChange={(e) => setDraft(e.target.value.slice(0, MAX))} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={2} placeholder="Write a message…" className="flex-1 resize-none border border-foreground/15 bg-card p-3 text-sm focus:border-foreground focus:outline-none" />
            <button type="submit" data-testid="conversation-send" disabled={!draft.trim() || sending} className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform hover:-translate-y-0.5 disabled:opacity-40" aria-label="Send"><Send size={14} /></button>
          </div>
          <p className="mt-2 text-right text-[10px] text-muted-foreground">{draft.length}/{MAX}</p>
        </form>
      ) : (
        <div className="border-t border-foreground/10 pt-6 text-center text-sm text-muted-foreground">Messaging closed 24 hours after check-out.</div>
      )}
    </div>
  );
};

export default Conversation;
